import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';

import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { HeavyHeaderComponent } from 'app/components/ui-kit/typography/heavy-header/heavy-header.component';
import { BaseSelectComponent } from 'app/components/ui-kit/inputs/base-select/base-select.component';
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { AircraftOption, aircraftOptionsNetJets } from 'app/constants/aircraft-constants';
import { ProviderNames } from 'app/constants/provider-constants';
import { UserService } from 'app/services/user.service';
import { TextButtonComponent } from 'app/components/ui-kit/buttons/text-button/text-button.component';

@Component({
  selector: 'app-invited-aircraft',
  standalone: true,
  imports: [
    PageWrapperComponent,
    HeavyHeaderComponent,
    CommonModule,
    FormsModule,
    BaseSelectComponent,
    BaseInputComponent,
    PrimaryBtnComponent,
    AlertMessageComponent,
    TextButtonComponent,
  ],
  templateUrl: './invited-aircraft.component.html',
  styleUrl: './invited-aircraft.component.scss'
})
export class InvitedAircraftComponent {
  aircraftOptions: AircraftOption[] = aircraftOptionsNetJets;
  defaultAircraft: AircraftOption | null = null;
  seatingConfiguration = 0;

  hasAircraft: boolean | null = null;
  isOtherAircraft = false;
  otherAircraft = '';

  error: string | null = null;
  isLoading = false;

  constructor(private userService: UserService, private router: Router) { }

  onSelectAircraft(aircraft: AircraftOption | null) {
    if (aircraft && aircraft.name === 'Other') {
      this.isOtherAircraft = true;
      this.defaultAircraft = null;
      this.otherAircraft = '';
      this.seatingConfiguration = 0;
      return;
    }
    this.isOtherAircraft = false;
    this.otherAircraft = '';
    this.defaultAircraft = aircraft;
    this.seatingConfiguration = aircraft ? aircraft.defaultSeats : 0;
  }

  viewList() {
    this.isOtherAircraft = false;
    this.otherAircraft = '';
  }

  onClickBack() {
    this.hasAircraft = null;
  }

  async continueWithoutAircraft() {
    this.error = null;
    this.isLoading = true;
    try {
      const profile = await this.userService.getUserProfile();
      const existing = Array.isArray(profile?.memberships) ? [...profile.memberships] : [];
      const id = crypto.randomUUID();
      existing.push({
        id,
        provider: 'independent',
        organization: 'independent',
        type: 'non-owner',
        defaultAircraft: null,
        seatingConfiguration: 0,
        statementVerified: true,
        statementStatus: 'trusted-contact',
        trustedContact: true
      });
      await this.userService.updateUserProfile({
        accountType: 'trusted-contact',
        memberships: existing,
        preferences: profile?.preferences || this.defaultPreferences(),
        preferredProviders: profile?.preferredProviders || [ProviderNames.ALL_INDEPENDENT]
      });
      this.router.navigate(['/onboard/get-started']);
    } catch {
      this.error = 'Failed to save membership';
    } finally {
      this.isLoading = false;
    }
  }

  async submit(form: NgForm) {
    this.error = null;

    if (form.invalid) {
      this.error = 'Please fill all required required fields';
      return;
    }

    this.isLoading = true;

    try {
      const profile = await this.userService.getUserProfile();
      const existing = Array.isArray(profile?.memberships) ? [...profile.memberships] : [];

      // Reuse the existing first membership id if present; otherwise create one
      const id = (existing[0]?.id as string | undefined) ?? crypto.randomUUID();

      const defaultAircraft = this.isOtherAircraft
        ? { name: this.otherAircraft, category: 'Other', defaultSeats: this.seatingConfiguration }
        : this.defaultAircraft;

      // Overwrite (upsert) the first slot
      existing[0] = {
        id,
        provider: 'independent',
        organization: 'independent',
        type: 'aircraft-owner',
        defaultAircraft,
        seatingConfiguration: this.seatingConfiguration,
        isOtherAircraft: this.isOtherAircraft,
        statementVerified: true,
        statementStatus: 'trusted-contact',
        trustedContact: true
      };

      // Ensure we only keep one membership
      if (existing.length > 1) existing.splice(1);

      await this.userService.updateUserProfile({
        accountType: 'trusted-contact',
        memberships: existing,
        preferences: profile?.preferences || this.defaultPreferences(),
        preferredProviders: profile?.preferredProviders || [ProviderNames.ALL_INDEPENDENT]
      });
      this.router.navigate(['/onboard/get-started']);
    } catch (err) {
      console.error(err);
      this.error = 'Failed to save membership';
    } finally {
      this.isLoading = false;
    }
  }

  private defaultPreferences() {
    return {
      children: { preference: false, usage: false },
      pets: { preference: false, usage: false },
      quiet: { preference: false, usage: false }
    };
  }

}
